import { CANCEL } from 'redux-saga';
import ExecutionEnvironment from 'exenv';

import { trackOperation } from 'events/ops/opsEvent';
import { trackError } from 'helpers/ErrorUtils';
import { FetchError } from 'middleware/fetchErrorMiddleware';
import type { UserOperation } from 'events/ops/opsEvent';
import type { ErrorEventInfo } from 'helpers/ErrorUtils';

/**
 * Deadline (ms) applied to a supplementary search content region fetch during
 * SSR. A request that does not complete within this window is classified as
 * degraded and the affected region is omitted from the page. Kept aggressive so
 * a slow region does not hold up the initial page render.
 */
export const SEARCH_CONTENT_TIMEOUT_MS = 2_000;

/**
 * Resolves the search-content deadline for the current environment. During SSR the
 * aggressive budget above bounds the initial page render. Once running client-side there
 * is no page-render budget to protect, so no deadline is applied — a client timeout only
 * produced spurious degradations on slower connections. `Infinity` signals "no timeout"
 * to {@link withTimeout}, which then wires cancellation only.
 */
export function getSearchContentTimeoutMs(canUseDOM: boolean = ExecutionEnvironment.canUseDOM): number {
  return canUseDOM ? Infinity : SEARCH_CONTENT_TIMEOUT_MS;
}

/**
 * Maximum number of client-side attempts for a degraded primary search request before a full-page
 * error is shown. Applies to every client-side primary search — the request re-issued after
 * hydration of a server-degraded page, and client navigation, pagination, and filter/sort requests.
 * Configurable in one place.
 */
export const SEARCH_RESULTS_RETRY_LIMIT = 3;

/**
 * Exponential backoff schedule (ms) spacing successive client-side retries of a
 * degraded primary search request. Each entry is strictly greater than the
 * preceding one; the array is indexed by attempt number.
 */
export const SEARCH_RESULTS_RETRY_BACKOFF_MS = [500, 1000, 2000];

/**
 * The outcome of an upstream (Olympus / Janus / ZCS) fetch once it has thrown.
 * `success` is represented by the absence of a thrown value, so the classifier
 * only ever distinguishes the two failure shapes.
 */
export type UpstreamResult = 'cancelled' | 'degraded';

/**
 * Detects a client-side network error thrown by the Fetch API. When `fetch()`
 * cannot complete the HTTP request at all — device loses connectivity, DNS
 * failure, user navigates away mid-request, or a bot abandons the connection —
 * it rejects with a `TypeError` whose message is browser-dependent:
 *
 *  - Chromium: "Failed to fetch"
 *  - Safari / WebKit: "Load failed"
 *  - Firefox: "NetworkError when attempting to fetch resource."
 *
 * These are NOT server-side failures and should not count toward operational
 * failure metrics.
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }
  const msg = error.message.toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('load failed') || msg.includes('networkerror');
}

/**
 * Shape of the Redux error state managed by reducers/errors.js.
 * Used by {@link isSuppressibleClientError} to inspect the error without
 * depending on the full reducer type.
 */
interface ReduxErrorState {
  type?: string;
  event?: unknown;
  message?: string;
  statusCode?: number;
}

/**
 * Determines whether a full-page error (Redux error state) was caused by a
 * client-side network TypeError.
 *
 * The signal is:
 *  - `error.type` === 'CLIENT'  (occurred in the browser, not during SSR)
 *  - `error.event` is a network TypeError per {@link isNetworkError}
 *
 * These errors should not count toward the ops ERROR_PAGE metric because they
 * represent bots or users losing connectivity, not actual service failures.
 */
export function isSuppressibleClientError(error: ReduxErrorState | null | undefined): boolean {
  if (!error || error.type !== 'CLIENT') {
    return false;
  }
  return isNetworkError(error.event);
}

/** Identifies which upstream service produced the degradation. */
export type UpstreamService = 'Janus' | 'Olympus' | 'ZCS';

interface DegradationReport {
  /** The degraded upstream service, identified by name in the error record. */
  service: UpstreamService;
  /** The ops operation whose failure metric should be emitted. */
  operation: UserOperation;
  /** Optional label for the omitted content region. */
  region?: string;
  /** The thrown value that triggered the degradation. */
  error: unknown;
}

/**
 * An AbortError tagged by our own timeout controller. A plain navigation abort
 * is a cancellation, while a timeout-tagged abort is a degradation.
 */
function isTimeoutAbort(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && (error as { isTimeout?: unknown }).isTimeout === true);
}

/**
 * Classifies any thrown fetch outcome as either `cancelled` or `degraded`,
 * giving the search and recommendation code paths a single shared definition.
 *
 *  - plain `AbortError` (customer-initiated navigation)        → `cancelled`
 *  - timeout-tagged abort (`error.isTimeout === true`)          → `degraded`
 *  - thrown `FetchError` (status outside 2xx)                   → `degraded`
 *  - network failure / connection reset / DNS                  → `degraded`
 *  - JSON parse failure / missing required fields              → `degraded`
 */
export function classifyUpstreamResult(error: unknown): UpstreamResult {
  if (error instanceof Error && error.name === 'AbortError') {
    return isTimeoutAbort(error) ? 'degraded' : 'cancelled';
  }
  // A thrown FetchError signals a non-2xx upstream response.
  if (error instanceof FetchError) {
    return 'degraded';
  }
  // Network failures, connection resets, and JSON parse failures all reach
  // here and are degradations.
  return 'degraded';
}

/**
 * Whether a degraded failure is worth retrying. Only meaningful for an error that
 * {@link classifyUpstreamResult} classified as `degraded`.
 *
 *  - timeout / connection reset / network error → `true`  (transient; a retry may succeed)
 *  - `FetchError` with a 5xx status             → `true`  (server-side, often transient)
 *  - `FetchError` with a 4xx status             → `false` (deterministic client error)
 *  - JSON parse failure (`SyntaxError`)         → `false` (the same body will fail to parse again)
 */
export function isRetryableDegradation(error: unknown): boolean {
  if (error instanceof FetchError) {
    const { status } = error as FetchError & { status?: number };
    return !(typeof status === 'number' && status >= 400 && status < 500);
  }
  if (error instanceof SyntaxError) {
    return false;
  }
  return true;
}

/**
 * Emits the failure metrics for a degraded upstream region. This is the single
 * choke point that guarantees a degradation is never silent and that
 * a throwing emitter never breaks rendering or omission.
 *
 * Must only be called when {@link classifyUpstreamResult} returns `degraded`;
 * cancellations skip emission entirely.
 *
 * Client-side network TypeErrors (client lost connectivity, bot abandoned
 * request) are suppressed from ops failure emission — they are not
 * server-side failures and inflate alarm metrics. During SSR, however, the
 * same TypeError shape genuinely indicates that the server could not reach
 * the upstream, so the failure metric is always emitted on the server.
 *
 * The error record is always logged for observability regardless of
 * environment.
 */
export function reportDegradation({ service, operation, region, error }: DegradationReport): void {
  try {
    const isServer = !ExecutionEnvironment.canUseDOM;
    if (isServer || !isNetworkError(error)) {
      trackOperation(operation, 'failure');
    }
    trackError('ERROR', `Degraded upstream: ${service}${region ? ` (${region})` : ''}`, error as ErrorEventInfo);
  } catch {
    // Emission failure must not break rendering or region omission.
  }
}

/**
 * Runs `run` with an AbortSignal that is aborted with a timeout-tagged
 * `AbortError` after `ms` milliseconds, enforcing a deadline on supplementary
 * search content fetches. The timer is always cleared in `finally`.
 *
 * A non-finite `ms` (e.g. the client-side search-content fetch, which has no
 * page-render budget to protect) means "no timeout": the request stays cancellable
 * via the redux-saga CANCEL hook below, but is never aborted by a timer.
 *
 * The timeout abort is tagged with `isTimeout` so {@link classifyUpstreamResult}
 * classifies it as `degraded` rather than `cancelled`.
 */
export function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>, ms: number = getSearchContentTimeoutMs()): Promise<T> {
  const controller = new AbortController();
  const timer = Number.isFinite(ms)
    ? setTimeout(() => {
        const error = new DOMException('Timed out', 'AbortError');
        (error as { isTimeout?: boolean }).isTimeout = true;
        controller.abort(error);
      }, ms)
    : undefined;
  const result = run(controller.signal).finally(() => clearTimeout(timer));
  // When this promise is awaited inside a redux-saga that gets cancelled (e.g. a new navigation
  // supersedes the request via takeLatest), redux-saga invokes this CANCEL hook so the in-flight
  // fetch is aborted rather than left running until it times out.
  Object.defineProperty(result, CANCEL, { value: () => controller.abort() });
  return result;
}

/**
 * Resolves after `ms` milliseconds, or early if `signal` aborts in the meantime — used to space
 * client-side retries of a degraded primary search request so a customer-initiated navigation during
 * the backoff window abandons the wait immediately rather than stalling the retry loop.
 *
 * It always resolves (never rejects); callers re-check `signal.aborted` after it settles to decide
 * whether to continue. The timer is cleared and the abort listener removed on settle.
 */
export function cancellableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>(resolve => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
